// Type imports don't end up in bundles
// eslint-disable-next-line no-restricted-syntax
import type { IClickstreamTriggerBlock } from '@zillow/universal-analytics';
import {
    CallbackSchedulerImpressionEvent,
    CallbackSchedulerInteractionEvent,
    HomeLoansDashboardImpressionEvent,
    HomeLoansDashboardInteractionEvent,
    HomeLoansDashboardViewEvent,
    REGISTERED_CLICKSTREAM_EVENTS,
} from '../events/registeredEvents';

export type TriggerLocation = Omit<IClickstreamTriggerBlock, 'trigger_type_nm'>;
export const HOME_LOANS_DASHBOARD_TRIGGER_BLOCKS: Record<
    HomeLoansDashboardInteractionEvent | HomeLoansDashboardViewEvent | HomeLoansDashboardImpressionEvent,
    TriggerLocation
> = {
    [REGISTERED_CLICKSTREAM_EVENTS.PRIMARY_CTA_CLICK]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_upsell_module',
        trigger_object_nm: 'home_loans_dashboard_upsell_module_primary_cta',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.SECONDARY_CTA_CLICK]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_upsell_module',
        trigger_object_nm: 'home_loans_dashboard_upsell_module_secondary_cta',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.RESOURCE_CARD_PRIMARY_CTA_CLICK]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_resource_card',
        trigger_object_nm: 'home_loans_dashboard_resource_card_primary_cta',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.RESOURCE_CARD_MODAL_BACKDROP_CLICK]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_resource_card_modal',
        trigger_object_nm: 'home_loans_dashboard_resource_card_modal_backdrop',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.RESOURCE_CARD_MODAL_CLOSE_CLICK]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_resource_card_modal',
        trigger_object_nm: 'home_loans_dashboard_resouce_card_modal_close_button',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.RESOURCE_CARD_MODAL_PRIMARY_CTA_CLICK]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_resource_card_modal',
        trigger_object_nm: 'home_loans_dashboard_resource_card_modal_primary_cta',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.TEAM_INFORMATION_CARD_CALL_CTA_CLICK]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_team_information_card',
        trigger_object_nm: 'home_loans_dashboard_team_information_card_call_cta',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.TEAM_INFORMATION_CARD_CALL_OR_TEXT_CTA_CLICK]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_team_information_card',
        trigger_object_nm: 'home_loans_dashboard_team_information_card_call_or_text_cta',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.TEAM_INFORMATION_CARD_EMAIL_CTA_CLICK]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_team_information_card',
        trigger_object_nm: 'home_loans_dashboard_team_information_card_email_cta',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.SAVED_INFORMATION_PRIMARY_CTA_CLICK]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_view_saved_information_card',
        trigger_object_nm: 'home_loans_dashboard_view_saved_information_card_primary_cta',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.UPSELL_MODULE_LOADED]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_upsell_module',
        trigger_object_nm: 'home_loans_dashboard_upsell_module',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.RESOURCE_CARD_MODULE_LOADED]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_resource_card',
        trigger_object_nm: 'home_loans_dashboard_resource_card_module',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.TEAM_INFORMATION_CARD_MODULE_LOADED]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_team_information_card',
        trigger_object_nm: 'home_loans_dashboard_team_information_card_module',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.VIEW_SAVED_INFORMATION_CARD_MODULE_LOADED]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard_view_saved_information_card',
        trigger_object_nm: 'home_loans_dashboard_view_saved_information_card_module',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.REDIRECT_TO_GOLDENEYE]: {
        trigger_location_nm: 'home_loans_dashboard_bounce_page',
        trigger_source_nm: 'home_loans_dashboard_bounce_page_redirect',
        trigger_object_nm: 'home_loans_dashboard_bounce_page_redirect_to_goldeneye',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.REDIRECT_TO_DASHBOARD]: {
        trigger_location_nm: 'home_loans_dashboard_bounce_page',
        trigger_source_nm: 'home_loans_dashboard_bounce_page_redirect',
        trigger_object_nm: 'home_loans_dashboard_bounce_page_redirect_to_dashboard',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.DASHBOARD_LOADED]: {
        trigger_location_nm: 'home_loans_dashboard',
        trigger_source_nm: 'home_loans_dashboard',
        trigger_object_nm: 'no_trigger_object',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.DASHBOARD_BOUNCE_LOADED]: {
        trigger_location_nm: 'home_loans_dashboard_bounce_page',
        trigger_source_nm: 'home_loans_dashboard_bounce_page',
        trigger_object_nm: 'no_trigger_object',
    },
};

export const CALLBACK_SCHEDULER_TRIGGER_BLOCKS: Record<
    CallbackSchedulerInteractionEvent | CallbackSchedulerImpressionEvent,
    TriggerLocation
> = {
    [REGISTERED_CLICKSTREAM_EVENTS.CALLBACK_SCHEDULER_BOOK_APPOINTMENT_CLICK]: {
        trigger_location_nm: 'zhl_callback_scheduler',
        trigger_source_nm: 'zhl_callback_scheduler_book_appointment',
        trigger_object_nm: 'zhl_callback_scheduler_book_appointment_button',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.CALLBACK_SCHEDULER_EXIT_CLICK]: {
        trigger_location_nm: 'zhl_callback_scheduler',
        trigger_source_nm: 'zhl_callback_scheduler_book_appointment',
        trigger_object_nm: 'zhl_callback_scheduler_close_scheduler_button',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.CALLBACK_SCHEDULER_APPOINTMENT_FAILED]: {
        trigger_location_nm: 'zhl_callback_scheduler',
        trigger_source_nm: 'zhl_callback_scheduler_appointment_failed',
        trigger_object_nm: 'no_trigger_object',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.CALLBACK_SCHEDULER_APPOINTMENT_BOOKER_LOADED]: {
        trigger_location_nm: 'zhl_callback_scheduler',
        trigger_source_nm: 'zhl_callback_scheduler_book_appointment',
        trigger_object_nm: 'no_trigger_object',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.CALLBACK_SCHEDULER_SUCCESS_DASHBOARD_BUTTON_CLICK]: {
        trigger_location_nm: 'zhl_callback_scheduler',
        trigger_source_nm: 'zhl_callback_scheduler_appointment_confirmed',
        trigger_object_nm: 'zhl_callback_scheduler_dashboard_button',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.CALLBACK_SCHEDULER_FAILURE_DASHBOARD_BUTTON_CLICK]: {
        trigger_location_nm: 'zhl_callback_scheduler',
        trigger_source_nm: 'zhl_callback_scheduler_appointment_failed',
        trigger_object_nm: 'zhl_callback_scheduler_preapproval_button',
    },
    [REGISTERED_CLICKSTREAM_EVENTS.CALLBACK_SCHEDULER_APPOINTMENT_CONFIRMED]: {
        trigger_location_nm: 'zhl_callback_scheduler',
        trigger_source_nm: 'zhl_callback_scheduler_appointment_confirmed',
        trigger_object_nm: 'no_trigger_object',
    },
};
export enum TRIGGER_TYPES {
    IMPRESSION = 'impression',
    INTERACTION = 'interaction',
    VIEW = 'view',
}
